// Persistent thin bar at the top of the mobile home view. Replaces the
// existing top button row (Logout / Settings / Theme / Messages /
// Dashboard) — those system controls move into a hamburger overflow so
// the top of the screen (the *hard zone* per Hoober's research, ~25%
// one-handed accuracy) holds glanceable display + a single hamburger
// instead of 5 frequently-tapped buttons.
//
// Contents (left → right):
//   ☰ hamburger        — only frequently-tapped element. Opens overflow.
//   32px portrait      — pure display.
//   Character name     — truncate with `...` if too long.
//   Mini HP/mana bar   — at-a-glance status; tap jumps to Stats tab.
//   🔔 notification    — top-right is tolerable for low-frequency,
//                        always-glanceable elements.
//
// Usage:
//   <MobileStickyHeader
//     char={char}
//     unreadCount={unreadMessages}
//     onHamburgerClick={() => setMobileMenuOpen(true)}
//     onMiniHpClick={() => setMobileTab('stats')}
//     onBellClick={() => setShowMessagePanel(true)}
//   />
//
// Theme-aware via CSS variables. Sticks to top: 0 with fixed position.

import React from 'react';
import useReducedMotion from './useReducedMotion';
import { t, DUR_BAR, EASE_STANDARD } from './transitions';

export default function MobileStickyHeader({
  char,
  unreadCount = 0,
  onHamburgerClick,
  onMiniHpClick,
  onBellClick,
  // 2026-04-29 — A.3 subview mode. When `backTo` is set, the hamburger is
  // replaced by a ← back button that calls backTo(). Title replaces the
  // mini HP bar with the subview name. Bell still renders. Used by every
  // subview (journal, combat, travel, shop, loot, npcs, timeline) so the
  // user always has a single-tap path back to the home view.
  backTo = null,
  title = null,
  // 2026-05-11 — Session 3 cleanup. Visible sync indicator: small dot
  // that pulses when a Convex write is in flight, fades when it lands.
  // MOBILE-REDESIGN.md §10 D&D Beyond cautionary tale ("sheet not
  // updating") — visible sync state is load-bearing trust infrastructure.
  // Default false so the dot is invisible until callers plumb the state.
  syncing = false,
  // 2026-05-11 — Session 9: optional suffix rendered after the subview
  // title in a smaller, dimmer font. Used by Shop for the persistent
  // currency display ("🏪 Shop · 47 gp"), and any future panel that
  // wants a glanceable status next to its title (crafting timers,
  // loot drop counts, etc.). Empty/null = no suffix render.
  titleSuffix = null,
}) {
  const reduceMotion = useReducedMotion();
  if (!char && !backTo) return null;
  const inSubview = !!backTo;
  const hpPct = Math.max(0, Math.min(100, ((char.currentHp || 0) / Math.max(1, char.maxHp || 1)) * 100));
  const tempHp = char.tempHp || 0;
  const tempPct = Math.max(0, Math.min(100, (tempHp / Math.max(1, char.maxHp || 1)) * 100));

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '50px',
        background: 'var(--bg2, #1e1624)',
        borderBottom: '1px solid var(--bs, #2a2033)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '0 8px',
        zIndex: 100,
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
      }}
    >
      {/* Left slot: hamburger on home view; back button in subviews. 44×44 hit area. */}
      <button
        onClick={inSubview ? backTo : onHamburgerClick}
        aria-label={inSubview ? 'Back' : 'Open menu'}
        style={{
          width: '44px',
          height: '44px',
          minWidth: '44px',
          background: 'transparent',
          border: 'none',
          color: 'var(--ts, #c9a227)',
          fontSize: inSubview ? '1.6rem' : '1.4rem',
          cursor: 'pointer',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {inSubview ? '←' : '☰'}
      </button>

      {/* Subview mode: title block instead of portrait + HP. */}
      {inSubview ? (
        <div
          style={{
            flex: 1,
            minWidth: 0,
            padding: '4px 6px',
            display: 'flex',
            alignItems: 'baseline',
            gap: '6px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          <span style={{
            fontSize: '0.95rem',
            fontWeight: 600,
            color: 'var(--tg, #e8d5b7)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontFamily: "'Cinzel', serif",
            letterSpacing: '0.5px',
            flexShrink: 0,
          }}>
            {title || ''}
          </span>
          {titleSuffix ? (
            <span style={{
              fontSize: '0.78rem',
              fontWeight: 500,
              color: 'var(--ts, #c9a227)',
              opacity: 0.85,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}>
              {titleSuffix}
            </span>
          ) : null}
        </div>
      ) : (<>
      {/* Portrait */}
      <div
        style={{
          width: '32px',
          height: '32px',
          minWidth: '32px',
          borderRadius: '50%',
          overflow: 'hidden',
          border: '1px solid var(--bs, #2a2033)',
          background: 'var(--bg1, #16101a)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.1rem',
        }}
      >
        {char.image ? (
          <img src={char.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          // 2026-04-30: same Twemoji SVG asset as the desktop portrait so
          // mobile and desktop avatars match. See public/mage.svg.
          <img src={(process.env.PUBLIC_URL || '') + '/mage.svg'} alt="Mage" style={{ width: '85%', height: '85%', objectFit: 'contain' }} />
        )}
      </div>

      {/* Name + mini HP — clickable, jumps to Stats tab */}
      <button
        onClick={onMiniHpClick}
        aria-label={`${char.name || 'Character'} status — open Stats tab`}
        style={{
          flex: 1,
          minWidth: 0,
          background: 'transparent',
          border: 'none',
          padding: '4px 6px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '2px',
          cursor: 'pointer',
          color: 'inherit',
          textAlign: 'left',
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <div
          style={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'var(--tg, #e8d5b7)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
            lineHeight: 1,
          }}
        >
          {char.name || 'Character'}
        </div>
        <div
          style={{
            position: 'relative',
            width: '90px',
            height: '8px',
            background: 'rgba(0,0,0,0.35)',
            border: '1px solid var(--bs, #2a2033)',
            borderRadius: '4px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: `${hpPct}%`,
              background: hpPct > 50 ? '#27c95a' : hpPct > 25 ? '#c9a227' : '#c92727',
              transition: reduceMotion ? 'none' : t(['width', 'background'], DUR_BAR, EASE_STANDARD),
            }}
          />
          {tempHp > 0 && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: `${hpPct}%`,
                height: '100%',
                width: `${Math.min(100 - hpPct, tempPct)}%`,
                background: '#87ceeb',
                opacity: 0.85,
              }}
            />
          )}
        </div>
      </button>
      </>)}

      {/* Sync indicator dot. Visible only while a write is in flight.
          Positioned just left of the bell so it stays in the same
          glanceable cluster as notifications. 8 px diameter — small
          enough not to draw attention when nothing is happening,
          large enough to read at a glance. A.4 polish will add a
          1.2 s keyframe pulse via the shared <style> element that
          lands then; for now the dot is static but visibly present. */}
      {syncing ? (
        <span
          aria-label="Syncing changes"
          role="status"
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: 'var(--tg, #d4a832)',
            boxShadow: '0 0 6px rgba(212, 168, 50, 0.6)',
            flexShrink: 0,
          }}
        />
      ) : null}

      {/* Bell with unread badge — top-right tolerated zone for low-freq glanceable */}
      <button
        onClick={onBellClick}
        aria-label={unreadCount > 0 ? `${unreadCount} unread messages` : 'Messages'}
        style={{
          position: 'relative',
          width: '44px',
          height: '44px',
          minWidth: '44px',
          background: 'transparent',
          border: 'none',
          color: 'var(--ts, #c9a227)',
          fontSize: '1.1rem',
          cursor: 'pointer',
          padding: 0,
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        🔔
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '6px',
              right: '4px',
              minWidth: '16px',
              height: '16px',
              padding: '0 4px',
              background: '#c92727',
              color: 'white',
              borderRadius: '8px',
              fontSize: '0.62rem',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
    </div>
  );
}
